import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { MousePointerClick, X, Pause, Play } from 'lucide-preact';
import { domToCanvas } from 'modern-screenshot';
import { api } from '@/lib/teaching/teacher-api';
import { pushToast } from '@/lib/toasts';
import { Btn } from './form';

// Workspace notes for the build loop (2026-10-17): a button bottom right
// turns on pick mode; hovering outlines any element, clicking adds it to the
// note. Picks survive moving between pages (pause picking, click the
// sidebar, resume). Saved notes are read by Ali with `teacher-cli notes`.

export type Kind = 'improve' | 'feature' | 'broken';
export interface Pick { page: string; selector: string; text: string; shot: string | null }

const open = signal(false);
const picking = signal(true);
const picks = signal<Pick[]>([]);
const kind = signal<Kind | null>(null);
const text = signal('');

export const KIND_LABEL: Record<Kind, string> = { improve: 'Improve this', feature: 'New feature here', broken: 'Broken' };

/** Ask the picker to outline an element once its page has rendered. */
const SHOW_KEY = 'tos.pick.show';
export function showPick(selector: string) {
  try { sessionStorage.setItem(SHOW_KEY, selector); } catch { /* storage off: show() below still runs on this page */ }
  pendingShow = selector;
}
let pendingShow: string | null = null;

const STABLE_ATTRS = ['data-nav', 'data-pick', 'data-testid', 'aria-label', 'name'];

/** A selector that finds the element again: anchored at the nearest
 *  ancestor with a stable attribute, else a tag path from <main>. */
export function selectorOf(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== document.body) {
    if (cur.id && !/\d{3,}/.test(cur.id)) { parts.unshift(`#${CSS.escape(cur.id)}`); break; }
    const attr = STABLE_ATTRS.find((a) => cur!.hasAttribute(a) && cur!.getAttribute(a));
    if (attr) {
      const sel = `${cur.tagName.toLowerCase()}[${attr}="${CSS.escape(cur.getAttribute(attr)!)}"]`;
      parts.unshift(sel);
      if (document.querySelectorAll(sel).length === 1) break;
    } else {
      const tag = cur.tagName.toLowerCase();
      if (tag === 'main') { parts.unshift('main'); break; }
      const same = cur.parentElement ? Array.from(cur.parentElement.children).filter((c) => c.tagName === cur!.tagName) : [];
      parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(cur) + 1})` : tag);
    }
    cur = cur.parentElement;
  }
  return parts.join(' > ');
}

function visibleText(el: Element): string {
  const t = (el as HTMLElement).innerText ?? el.textContent ?? '';
  const label = el.getAttribute('aria-label') ?? el.getAttribute('title') ?? '';
  return (t.replace(/\s+/g, ' ').trim() || label).slice(0, 200);
}

const scrolls = (el: Element) => {
  const s = getComputedStyle(el);
  return /(auto|scroll|hidden)/.test(s.overflowY + s.overflowX) && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
};

/** A picture of the spot with some of the page around it and an outline
 *  on the spot itself, so Ali can see where it sits. Renders a nearby
 *  block (never a scrolled container, whose copy would show its top). */
async function shotOf(el: Element): Promise<string | null> {
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  let host: Element = el;
  for (let up = el.parentElement; up && up !== document.body && up.tagName !== 'MAIN' && !scrolls(up); up = up.parentElement) {
    host = up;
    const h = up.getBoundingClientRect();
    if (h.width >= Math.max(320, r.width + 80) && h.height >= Math.max(160, r.height + 60)) break;
  }
  const hr = host.getBoundingClientRect();
  try {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const full = await domToCanvas(host, { scale: dpr, backgroundColor: '#ffffff', filter: (n) => !(n instanceof Element && n.hasAttribute('data-picker-ui')) });
    // Crop to the spot plus a margin, then fit into 640x400.
    const pad = 48;
    const x0 = Math.max(0, r.left - hr.left - pad), y0 = Math.max(0, r.top - hr.top - pad);
    const x1 = Math.min(hr.width, r.right - hr.left + pad), y1 = Math.min(hr.height, r.bottom - hr.top + pad);
    const w = x1 - x0, h = y1 - y0;
    const fit = Math.min(1, 640 / w, 400 / h);
    const out = document.createElement('canvas');
    out.width = Math.round(w * fit * dpr); out.height = Math.round(h * fit * dpr);
    const ctx = out.getContext('2d')!;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(full, x0 * dpr, y0 * dpr, w * dpr, h * dpr, 0, 0, out.width, out.height);
    const k = fit * dpr;
    ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = Math.max(2, 3 * dpr * fit);
    ctx.strokeRect((r.left - hr.left - x0) * k - 2, (r.top - hr.top - y0) * k - 2, r.width * k + 4, r.height * k + 4);
    const url = out.toDataURL('image/jpeg', 0.72);
    return url.length < 400_000 ? url : null;
  } catch { return null; }
}

const inPicker = (t: EventTarget | null) => t instanceof Element && !!t.closest('[data-picker-ui]');

export function Picker() {
  const [loc] = useLocation();
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [flash, setFlash] = useState<DOMRect | null>(null);
  const [saving, setSaving] = useState(false);
  const busy = useRef(0);
  const active = open.value && picking.value;

  // Pick mode: outline under the pointer; a click picks instead of acting.
  useEffect(() => {
    if (!active) { setHover(null); return; }
    const move = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      setHover(el && !inPicker(el) ? el.getBoundingClientRect() : null);
    };
    const block = (e: Event) => { if (!inPicker(e.target)) { e.preventDefault(); e.stopPropagation(); } };
    const click = (e: MouseEvent) => {
      if (inPicker(e.target)) return;
      e.preventDefault(); e.stopPropagation();
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || inPicker(el)) return;
      const pick: Pick = { page: location.pathname + location.search, selector: selectorOf(el), text: visibleText(el), shot: null };
      picks.value = [...picks.value, pick];
      setFlash(el.getBoundingClientRect());
      setTimeout(() => setFlash(null), 500);
      busy.current++;
      void shotOf(el).then((shot) => {
        picks.value = picks.value.map((p) => (p === pick ? { ...pick, shot } : p));
      }).finally(() => { busy.current--; });
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') picking.value = false; };
    document.addEventListener('pointermove', move, true);
    document.addEventListener('pointerdown', block, true);
    document.addEventListener('mousedown', block, true);
    document.addEventListener('click', click, true);
    document.addEventListener('submit', block, true);
    document.addEventListener('keydown', key, true);
    const clear = () => setHover(null);
    window.addEventListener('scroll', clear, true);
    return () => {
      document.removeEventListener('pointermove', move, true);
      document.removeEventListener('pointerdown', block, true);
      document.removeEventListener('mousedown', block, true);
      document.removeEventListener('click', click, true);
      document.removeEventListener('submit', block, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', clear, true);
    };
  }, [active]);

  // "Show" from the notes list: outline the element once the page renders.
  useEffect(() => {
    let sel = pendingShow;
    try { sel = sessionStorage.getItem(SHOW_KEY) ?? sel; sessionStorage.removeItem(SHOW_KEY); } catch { /* fine */ }
    pendingShow = null;
    if (!sel) return;
    let tries = 0;
    const t = setInterval(() => {
      let el: Element | null = null;
      try { el = document.querySelector(sel!); } catch { clearInterval(t); return; }
      if (el) {
        clearInterval(t);
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => setFlash(el!.getBoundingClientRect()), 400);
        setTimeout(() => setFlash(null), 3400);
      } else if (++tries > 30) {
        clearInterval(t);
        pushToast({ tone: 'warn', title: 'That spot is not on this page any more', description: 'The page has changed since the note was made.' });
      }
    }, 150);
    return () => clearInterval(t);
  }, [loc]);

  const cancel = () => { open.value = false; picks.value = []; kind.value = null; text.value = ''; picking.value = true; };
  const save = async () => {
    if (!kind.value) { pushToast({ tone: 'warn', title: 'Choose what kind of note this is' }); return; }
    if (!text.value.trim() && !picks.value.length) { pushToast({ tone: 'warn', title: 'Pick a spot or write something' }); return; }
    setSaving(true);
    try {
      for (let i = 0; busy.current > 0 && i < 40; i++) await new Promise((r) => setTimeout(r, 100));
      await api('POST', '/api/t/workspace-notes', { kind: kind.value, text: text.value, picks: picks.value });
      pushToast({ tone: 'success', title: 'Note saved', description: 'Ali will see it with every spot you picked.' });
      cancel();
    } catch (e) {
      pushToast({ tone: 'error', title: 'Could not save the note', description: (e as Error).message });
    } finally { setSaving(false); }
  };

  const box = (r: DOMRect, cls: string) => (
    <div class={'fixed pointer-events-none z-[120] rounded-[4px] ' + cls} style={{ left: r.left - 2, top: r.top - 2, width: r.width + 4, height: r.height + 4 }} />
  );

  if (!open.value) {
    return (
      <button type="button" data-picker-ui data-picker-open onClick={() => { open.value = true; picking.value = true; }}
        title="Point at something and tell Ali" aria-label="Make a note for Ali"
        class="fixed bottom-4 right-4 z-[100] h-11 w-11 rounded-full shadow-lg bg-[var(--color-accent)] flex items-center justify-center" style={{ color: '#ffffff' }}>
        <MousePointerClick size={20} />
      </button>
    );
  }

  return (
    <>
      {active && hover && box(hover, 'border-2 border-[var(--color-accent)] bg-[var(--color-accent)]/10')}
      {flash && box(flash, 'border-[3px] border-[#f59e0b] bg-[#f59e0b]/15')}
      <div data-picker-ui data-picker-tray class="fixed bottom-4 right-4 z-[130] w-[min(340px,calc(100vw-32px))] max-h-[min(560px,calc(100dvh-32px))] flex flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl text-[var(--color-text)]">
        <div class="flex items-center gap-2 px-4 pt-3 pb-2">
          <div class="text-[14px] font-semibold">Note for Ali</div>
          <button type="button" onClick={cancel} aria-label="Close and discard" class="ml-auto p-1 rounded hover:bg-[var(--color-elevated)]"><X size={16} /></button>
        </div>
        <div class="px-4 text-[12px] text-[var(--color-text-muted)]">
          {picking.value ? 'Click anything on the page to point at it. Pause to move to another page.' : 'Paused. Clicks work as normal, so you can change page.'}
        </div>
        <div class="px-4 pt-2">
          <button type="button" data-picker-toggle onClick={() => { picking.value = !picking.value; }}
            class={'h-8 px-3 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 border ' + (picking.value ? 'border-[var(--color-accent)] text-[var(--color-accent)] bg-[var(--color-accent-soft)]' : 'border-[var(--color-border)]')}>
            {picking.value ? <><Pause size={14} /> Pause picking</> : <><Play size={14} /> Pick more</>}
          </button>
        </div>
        <div class="px-4 pt-3 flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
          {picks.value.length === 0 && <div class="text-[12.5px] text-[var(--color-text-muted)]">No spots picked yet.</div>}
          {picks.value.map((p, i) => (
            <div key={i} data-picker-pick class="flex gap-2 items-start rounded-lg border border-[var(--color-border)] p-1.5">
              <div class="w-16 h-12 shrink-0 rounded bg-[var(--color-elevated)] overflow-hidden flex items-center justify-center">
                {p.shot ? <img src={p.shot} alt="" class="max-w-full max-h-full object-contain" /> : <span class="text-[10px] text-[var(--color-text-muted)]">{i + 1}</span>}
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-[12px] truncate">{p.text || '(no text)'}</div>
                <div class="text-[11px] text-[var(--color-text-muted)] truncate">{p.page}</div>
              </div>
              <button type="button" aria-label="Remove this spot" onClick={() => { picks.value = picks.value.filter((_, j) => j !== i); }} class="p-1 rounded hover:bg-[var(--color-elevated)]"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div class="px-4 pt-3 flex flex-wrap gap-1.5">
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
            <button key={k} type="button" data-kind={k} onClick={() => { kind.value = k; }}
              class={'h-8 px-2.5 rounded-lg text-[12.5px] border ' + (kind.value === k ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-semibold' : 'border-[var(--color-border)]')}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <div class="px-4 pt-2">
          <textarea value={text.value} onInput={(e) => { text.value = (e.target as HTMLTextAreaElement).value; }} rows={3}
            placeholder="What should change? Say it how you'd say it to Ali."
            class="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-2 text-[13px] resize-y" />
        </div>
        <div class="px-4 py-3 flex gap-2 justify-end">
          <Btn onClick={cancel}>Discard</Btn>
          <Btn kind="primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : `Save note${picks.value.length ? ` (${picks.value.length})` : ''}`}</Btn>
        </div>
      </div>
    </>
  );
}
